import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import Button from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import {
  createViolationType,
  editViolationType,
  fetchSystemIcons,
  fetchViolationType,
  VIOLATION_TYPES_CREATE_SUCCESS,
  VIOLATION_TYPES_EDIT_SUCCESS,
} from '@actions/violationType';

import { getEmptyErrorState } from '@utils/AppUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator, { CharacterType } from '@utils/ErrorMessageGenerator';

import './style.scss';

type IconChoices = Array<{ value: any; iconURL: string }>;

interface IViolationTypeFormProps {
  show: boolean;
  onClose?: (e: any) => void;
  onSubmit?: (e: any) => void;
  violationTypeId?: number;
  // from redux
  fetchSystemIcons: TFetchSystemIcons;
  fetchViolationType: TFetchViolationType;
  createViolationType: TCreateViolationType;
  editViolationType: TEditViolationType;
  currentViolationType: ISystemXerceViolationTypeResponse;
  systemIcons: ISystemIcon[];
}

interface IViolationTypeFormState {
  violationType: ISystemXerceViolationTypeRequest;
  iconChoices: IconChoices;
  isEdit: boolean;
  error: {
    label: IFieldValidation;
    icon: IFieldValidation;
  };
}

const cyberdyneValidator = new CyberdyneValidator();
const errMsgGen = new ErrorMessageGenerator();

class ViolationTypeForm extends React.Component<IViolationTypeFormProps, IViolationTypeFormState> {
  constructor(props: IViolationTypeFormProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentWillReceiveProps(nextProps: IViolationTypeFormProps) {
    const { violationTypeId } = nextProps;
    const isEdit = !!violationTypeId;
    const isEditAndSystemIconsAndViolationTypePresent: boolean =
      isEdit && !!nextProps.currentViolationType && !!nextProps.systemIcons;
    const areSystemIconsUpdated: boolean = nextProps.systemIcons !== this.props.systemIcons;
    const hasModalOpened: boolean = nextProps.show !== this.props.show && nextProps.show;

    if (hasModalOpened) {
      this.setState({
        ...this.getEmptyState(),
        isEdit,
      });

      if (isEdit) {
        this.props.fetchViolationType(violationTypeId);
      }

      this.props.fetchSystemIcons();
    }

    if (areSystemIconsUpdated) {
      this.setState({
        iconChoices: this.getAvailableIconChoices(nextProps),
      });
    }

    if (isEditAndSystemIconsAndViolationTypePresent) {
      const { systemIcons, currentViolationType } = nextProps;

      const icon = this.getSystemIconByUrl(systemIcons, currentViolationType);

      this.setState({
        violationType: this.getCurrentViolationTypeFromProps(nextProps),
        iconChoices: [
          ...this.getAvailableIconChoices(nextProps),
          { iconURL: icon && icon.fileUrl, value: icon && icon.id },
        ],
      });
    }
  }

  getSystemIconByUrl(systemIcons: ISystemIcon[], currentViolationType: ISystemXerceViolationTypeResponse) {
    const icon = systemIcons.find(
      (systemIcon) =>
        currentViolationType.iconUrl && currentViolationType.iconUrl.split('?')[0] === systemIcon.fileUrl.split('?')[0]
    );

    return icon;
  }

  getEmptyState = () => ({
    violationType: {
      iconId: null,
      isAvailableForActivation: true,
      skipFollowUpInspection: false,
      isCustom: true,
      label: '',
    },
    iconChoices: [],
    isEdit: false,
    error: {
      label: getEmptyErrorState(),
      icon: getEmptyErrorState(),
    },
  });

  getCurrentViolationTypeFromProps = (nextProps: IViolationTypeFormProps) => {
    const { currentViolationType, systemIcons } = nextProps;
    const icon = this.getSystemIconByUrl(systemIcons, currentViolationType);
    let iconId = null;
    if (icon) {
      iconId = icon.id;
    }
    const { isAvailableForActivation, isCustom, label, skipFollowUpInspection } = currentViolationType;

    return {
      isAvailableForActivation,
      skipFollowUpInspection,
      isCustom,
      label,
      iconId,
    };
  };

  getAvailableIconChoices = (nextProps: IViolationTypeFormProps) => {
    const { systemIcons } = nextProps;

    return systemIcons
      .filter((systemIcon) => systemIcon.isAvailableForUse)
      .map((availableSystemIcon) => ({
        iconURL: availableSystemIcon.fileUrl,
        value: availableSystemIcon.id,
      }));
  };

  onClose = (e) => {
    this.setState(
      (prevState) => ({
        ...this.getEmptyState(),
        iconChoices: prevState.iconChoices,
      }),
      () => this.props.onClose(e)
    );
  };

  validateForm = (): boolean => {
    let isValid = true;
    const { violationType } = this.state;
    const error = this.getEmptyState().error;

    if (cyberdyneValidator.isEmptyString(violationType.label)) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errMsgGen.getMandatoryFieldMessage('Violation Type');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(violationType.label, 50)) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errMsgGen.getCharacterLimitMessage('Violation Type', 50);

      isValid = false;
    } else if (!cyberdyneValidator.isAlphaWithSpaces(violationType.label)) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errMsgGen.getLimitedCharacterTypeMessage(
        'Violation Type',
        CharacterType.ALPHA_WITH_SPACES
      );

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyValue(violationType.iconId)) {
      error.icon.errorState = IValidationState.ERROR;
      error.icon.errorMessage = 'The Icon is required.';

      isValid = false;
    }

    this.setState({
      error,
    });

    return isValid;
  };

  onSubmit = (e: React.SyntheticEvent<MouseEvent>) => {
    if (this.validateForm()) {
      const { violationType } = this.state;
      if (this.state.isEdit) {
        const { violationTypeId } = this.props;
        const violationTypeEditObject: IXerceViolationTypeEditRequest = {
          label: violationType.label,
          iconId: violationType.iconId,
          skipFollowUpInspection: violationType.skipFollowUpInspection,
        };

        this.props.editViolationType(violationTypeId, violationTypeEditObject).then((action) => {
          if (action.type === VIOLATION_TYPES_EDIT_SUCCESS) {
            this.props.onSubmit(e);
          }
        });
      } else {
        this.props.createViolationType(violationType).then((action) => {
          if (action.type === VIOLATION_TYPES_CREATE_SUCCESS) {
            this.props.onSubmit(e);
          }
        });
      }
    }
  };

  handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;

    this.setState((prevState) => ({
      violationType: {
        ...prevState.violationType,
        [name]: value,
      },
    }));
  };

  iconChooser = (iconOptions: IconChoices, currentValue: any, disabled?: boolean) => {
    const activeColor = '#c7541e';
    const inactiveColor = '#d1d1d1';
    const handleChange = (value) => {
      if (!disabled) {
        this.setState((prevState) => ({
          violationType: {
            ...prevState.violationType,
            iconId: value,
          },
        }));
      }
    };

    return (
      <div className={classnames('icon-chooser')}>
        {iconOptions &&
          iconOptions.map((option, index) => (
            <div
              className={classnames('icon-chooser__icon')}
              key={index}
              onClick={(e) => handleChange(option.value)}
              style={{
                background: `url('${option.iconURL}') ${
                  option.value === currentValue ? activeColor : inactiveColor
                } center / contain no-repeat`,
                height: '34px',
                width: '34px',
                maxWidth: '34px',
                maxHeight: '34px',
              }}
            />
          ))}
      </div>
    );
  };

  renderTitle = () => {
    const titleText = this.state.isEdit ? 'Edit Violation Type' : 'Create Violation Type';

    return titleText;
  };

  renderBody = () => {
    const twoStateOptions = [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];

    const skipFollowUpInspectionOptions = [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];

    const isIconAvailable = this.props.systemIcons?.some((icon) => icon.isAvailableForUse);
    let iconErrorMessage;

    if (!isIconAvailable) {
      iconErrorMessage = 'Please contact technical support for additional icons before creating new Violation Types.';
    }

    return (
      <div className={classnames('violation-type-form__body')}>
        <Input
          width={12}
          field={{
            label: 'Label',
            type: CustomFieldType.TEXT,
            value: this.state.violationType.label,
            isRequired: true,
          }}
          name="label"
          error={this.state.error.label}
          onChange={this.handleChange}
        />
        <Input
          width={12}
          field={{
            label: 'Available for Activation?',
            type: OtherFieldType.RADIOBUTTON,
            value: this.state.violationType.isAvailableForActivation,
            isRequired: true,
          }}
          disabled={true}
          valueLabelOptions={twoStateOptions}
          name="isAvailableForActivation"
          onChange={this.handleChange}
        />
        <div className="field-section">
          {!iconErrorMessage?.length ? (
            <Input
              width={12}
              field={{
                label: 'Icon',
                type: CustomFieldType.TEXT,
                value: null,
                isRequired: true,
              }}
              renderField={(name, onChange) =>
                this.iconChooser(
                  this.state.iconChoices,
                  this.state.violationType.iconId,
                  !this.state.violationType.isCustom
                )
              }
              error={this.state.error.icon}
              name="icon"
              onChange={() => null}
            />
          ) : (
            <div className="icon-error">
              <span className="label">Icon</span>
              <span className="error-message">{iconErrorMessage}</span>
            </div>
          )}
        </div>
        <Input
          width={12}
          field={{
            label: `Allow case to remain open without a
               follow-up inspection scheduled?`,
            type: OtherFieldType.RADIOBUTTON,
            value: this.state.violationType.skipFollowUpInspection,
            isRequired: false,
          }}
          valueLabelOptions={skipFollowUpInspectionOptions}
          name="skipFollowUpInspection"
          onChange={this.handleChange}
        />
      </div>
    );
  };

  renderFooter = () => {
    const submitButtonText = this.state.isEdit ? 'Save' : 'Create Violation Type';

    return (
      <div className={classnames('violation-type-form__footer')}>
        <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onClose} content="Cancel" />
        <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit} content={submitButtonText} />
      </div>
    );
  };

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.onClose}
        titleElement={this.renderTitle()}
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className={classnames('violation-type-form')}
      />
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  systemIcons: state.violationType.systemIcons,
  currentViolationType: state.violationType.currentViolationType,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchSystemIcons: () => dispatch(fetchSystemIcons()),
  createViolationType: (violationType: ISystemXerceViolationTypeRequest) =>
    dispatch(createViolationType(violationType)),
  editViolationType: (violationTypeId: number, violationType: ISystemXerceViolationTypeRequest) =>
    dispatch(editViolationType(violationTypeId, violationType)),
  fetchViolationType: (violationTypeId: number) => dispatch(fetchViolationType(violationTypeId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ViolationTypeForm);
